import React, { useState, useEffect, useMemo } from "react";
import Swal from "sweetalert2";
import FiltersPanel from "../Querys/FiltersPanel/FiltersPanel";
import QueriesTable from "../Querys/Table/QueriesTable";
import BugDetailsDrawer from "./BugDetailsDrawer";
import {
  getBugReportsList,
  updateTicketStatus,
  deleteTicketRecord,
} from "../../Backend/querys";
import {
  pageShellClass,
  pageContentClass,
} from "../../Container/PageShell/pageShellClasses";

const BugsReports = () => {
  const [bugs, setBugs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedIds, setSelectedIds] = useState([]);
  const [showFilters, setShowFilters] = useState(false);
  const [selectedBug, setSelectedBug] = useState(null);

  const [filters, setFilters] = useState({
    search: "",
    category: "ALL", // Maps to Severity (Medium, etc.)
    status: "ALL", // Maps to unresolved/resolved
    priority: "ALL", // Maps to P1, etc.
  });

  const fetchBugs = async () => {
    setLoading(true);
    const result = await getBugReportsList();
    if (result.success) {
      // Mapping Firebase Bug Schema
      const formatted = result.data.map((item) => ({
        ...item,
        sender: item.companyId || "Public Reporter",
        email: "Technical Report",
        formTitle: item.title || "Untitled Bug",
        description: item.actualResult || "No details",
        formCategory: item.severity || "Medium",
        submittedAt: item.createdAt || new Date().toISOString(),
        status: item.status || "unresolved",
        priority: item.priority || "P1",
      }));
      setBugs(formatted);
    }
    setLoading(false);
  };

  useEffect(() => {
    fetchBugs();
  }, []);

  const filteredBugs = useMemo(() => {
    return bugs.filter((b) => {
      const s = filters.search.toLowerCase();
      const matchSearch =
        b.formTitle.toLowerCase().includes(s) ||
        b.description.toLowerCase().includes(s);
      const matchSeverity =
        filters.category === "ALL" || b.formCategory === filters.category;
      const matchStatus =
        filters.status === "ALL" || b.status === filters.status;
      const matchPriority =
        filters.priority === "ALL" || b.priority === filters.priority;

      return matchSearch && matchSeverity && matchStatus && matchPriority;
    });
  }, [bugs, filters]);

 const handleAction = async (actionType, id, newValue) => {
    if (actionType === "delete") {
      const { isConfirmed } = await Swal.fire({
        title: "Delete Bug Report?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#EF4444",
      });
      if (!isConfirmed) return;
      setBugs((prev) => prev.filter((b) => b.id !== id));
      await deleteTicketRecord("bug", id);
      
    } else if (actionType === "updateStatus") {
      setBugs((prev) =>
        prev.map((b) => (b.id === id ? { ...b, status: newValue } : b)),
      );
      await updateTicketStatus("bug", id, { status: newValue });
      
    } else if (actionType === "reply") {
      const targetQuery = bugs.find((b) => b.id === id);
      
      // ✅ FIX 1: Look inside standard nested objects (like reporter.email) just in case
      const toEmail = targetQuery?.reporter?.email || targetQuery?.email || targetQuery?.userEmail;

      // ✅ FIX 2: Strict Validation - Must contain an '@' symbol
      if (!toEmail || !toEmail.includes("@")) {
        return Swal.fire({
          icon: "warning",
          title: "Invalid Email Address",
          text: `The system found "${toEmail || 'Nothing'}" instead of a valid email.`,
          confirmButtonColor: "#0B1C2D",
        });
      }

      const config = {
        company: "ContactHQ", // ✅ FIX 3: Removed "Team" from here to prevent "Team ContactHQ Team"
        sender: targetQuery?.reporter?.name || targetQuery?.sender || targetQuery?.name || "Customer",
        form: targetQuery?.title || targetQuery?.subject || targetQuery?.formTitle || "Your Query",
        website: "https://contacthq.in",
      };

      const subject = `${config.company} Support - Regarding: ${config.form}`;
      const bodyLines = [
        `Hello ${config.sender},`,
        ``,
        `Thank you for reaching out to us regarding "${config.form}".`,
        ``,
        `We have successfully received your bug report. Our technical team is currently reviewing your details, and we will get back to you very soon.`,
        ``,
        `Best regards,`,
        `The ${config.company} Team`, // Signature will now render perfectly
        config.website,
      ];
      
      const encodedSubject = encodeURIComponent(subject);
      const encodedBody = encodeURIComponent(bodyLines.join("\n"));
      const isGmail = toEmail.toLowerCase().includes("@gmail.com");
      
      if (isGmail) {
        window.open(
          `https://mail.google.com/mail/?view=cm&fs=1&to=${toEmail}&su=${encodedSubject}&body=${encodedBody}`,
          "_blank",
        );
      } else {
        window.location.href = `mailto:${toEmail}?subject=${encodedSubject}&body=${encodedBody}`;
      }
    }
  };

  return (
    <div className={pageShellClass}>
      <div className={pageContentClass}>
      <header className="flex justify-between items-center mb-6 lg:mb-8 gap-3">
        <div className="hidden lg:block">
          <h1 className="text-3xl font-black text-[#0B1C2D] tracking-tight uppercase">
            Bug <span className="text-red-500">Reports</span>
          </h1>
          <p className="text-slate-400 text-sm font-medium">
            Tracking {bugs.length} engineering issues.
          </p>
        </div>
        <p className="lg:hidden text-xs font-bold text-slate-400 uppercase tracking-widest">
          {bugs.length} issues
        </p>
        <button
          onClick={() => setShowFilters(!showFilters)}
          className={`p-3 rounded-2xl border active:scale-95 ${showFilters ? "bg-[#0B1C2D] text-white" : "bg-white text-slate-400"}`}
        >
          <span className="material-symbols-outlined">filter_list</span>
        </button>
      </header>

      {showFilters && (
        <FiltersPanel
          filters={filters}
          setFilters={setFilters}
          categories={["Critical", "High", "Medium", "Low"]}
        />
      )}

      <QueriesTable
        data={filteredBugs}
        loading={loading}
        selectedIds={selectedIds}
        setSelectedIds={setSelectedIds}
        onRowClick={setSelectedBug}
        onAction={handleAction}
      />

      <BugDetailsDrawer
        bug={selectedBug}
        isOpen={!!selectedBug}
        onClose={() => setSelectedBug(null)}
        onAction={handleAction}
      />
      </div>
    </div>
  );
};

export default BugsReports;
